import { P2GAlert, P2GBox, P2GButton, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { Currency } from '~/lib/types/basket-types';
import { toDisplayCurrency } from '~/lib/prices';
import BasketItemContainer from './basket-item-container';
import { lazy, Suspense } from 'react';

const CmsContent = lazy(() => import('~/components/core/content/cms-content'));

type props = {
  currency: Currency;
  wrapper: (c: React.ReactNode) => React.ReactNode;
  underpayment?: number;
};

const Underpayment = ({ wrapper, underpayment, currency }: props) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);

  return (
    underpayment && (
      <BasketItemContainer
        wrapper={wrapper}
        dataTestId="surcharges-wrapper"
        title={
          <P2GGrid container spacing={1} alignItems="center">
            <P2GGrid item flexGrow={1}>
              <P2GText colour="primary" fontWeight="bold">
                {t('UNDERPAYMENT_TITLE', { defaultValue: 'Outstanding Surcharge on Previous Order(s)' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <P2GText variant="body1" colour="primary" fontWeight="bold">
                {toDisplayCurrency(currency, underpayment, 2)}
              </P2GText>
            </P2GGrid>
          </P2GGrid>
        }
        left={
          <Suspense>
            <CmsContent contentKey="surcharge-message">
              <P2GBox sx={(t) => ({ display: 'flex', gap: t.spacing(1), flexDirection: 'column' })}>
                <P2GText variant="body1" fontWeight="bold">
                  A surcharge has been raised for one of your previous orders. You can see a breakdown of this{' '}
                  <P2GLink href="/myaccount/mysurcharges" target="_blank">
                    here
                  </P2GLink>
                  .
                </P2GText>
                <P2GText variant="body1">
                  A surcharge is an additional payment required by the courier. The amount owed has been added to your
                  basket and you won't be able to purchase another parcel without paying this charge. You can book
                  another parcel and pay any outstanding surcharges at the same time.
                </P2GText>
                <P2GText variant="body1">
                  We have sent you an email about any additional charges, but for more information click ‘Find Out
                  More’.
                </P2GText>
              </P2GBox>
            </CmsContent>
          </Suspense>
        }
        right={
          <P2GAlert type="info">
            <CmsContent contentKey="what-is-a-surcharge-message">
              <P2GBox sx={(t) => ({ display: 'flex', gap: t.spacing(1), flexDirection: 'column' })}>
                <P2GText variant="body1" fontWeight="bold">
                  What is a surcharge?
                </P2GText>
                <P2GText variant="body1">
                  A surcharge is where the courier asks for additional funds, for example, if the parcel is larger than
                  expected or has been packaged incorrectly
                </P2GText>
              </P2GBox>
            </CmsContent>
            <P2GBox mt={3}>
              <P2GButton href="/surcharge-info" target="_blank">
                {t('UNDERPAYMENT_FIND_OUT_MORE', { defaultValue: 'Find Out More' })}
              </P2GButton>
            </P2GBox>
          </P2GAlert>
        }
      />
    )
  );
};
export default Underpayment;
